// src/GroomerMatrix.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  InputAdornment,
  Divider,
  Alert,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { format, eachDayOfInterval, isValid, parse } from "date-fns";
import PrintIcon from "@mui/icons-material/Print";

/**
 * GroomerMatrix Component - Displays groomer performance matrix by date
 * Shows appointments, pets, and revenue per groomer per day with totals and goals
 */
const GroomerMatrix = () => {
  // State variables for date range and data
  const [startDate, setStartDate] = useState(
    format(new Date(), "yyyy-MM-dd")
  ); // Default to today
  
  // Default end date to 7 days from today
  const [endDate, setEndDate] = useState(() => {
    const nextWeek = new Date();
    nextWeek.setDate(nextWeek.getDate() + 7);
    return format(nextWeek, "yyyy-MM-dd");
  });
  const [dailyTarget, setDailyTarget] = useState(780); // Default daily target amount
  const [matrixData, setMatrixData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reportGenerated, setReportGenerated] = useState(false);

  // Track dates in the selected range
  const [datesInRange, setDatesInRange] = useState([]);

  // Effect to update dates in range when start/end dates change
  useEffect(() => {
    if (isValid(new Date(startDate)) && isValid(new Date(endDate))) {
      // Parse dates with explicit pattern to avoid timezone issues
      // This ensures dates are interpreted as midnight in local timezone
      const start = parse(startDate, 'yyyy-MM-dd', new Date());
      
      // For end date, ensure we include the full day by setting time to 23:59:59
      let end = parse(endDate, 'yyyy-MM-dd', new Date());
      end.setHours(23, 59, 59, 999);
      
      // Only calculate if end date is after or equal to start date
      if (end >= start) {
        const dateRange = eachDayOfInterval({
          start,
          end,
        });
        
        // Log dates to verify correct range
        console.log("Date range calculated:", dateRange.map(d => format(d, 'yyyy-MM-dd')));
        
        setDatesInRange(dateRange);
      }
    }
  }, [startDate, endDate]);

  // Handle daily target amount change
  const handleDailyTargetChange = (e) => {
    const value = e.target.value;
    const numValue = parseFloat(value);
    setDailyTarget(isNaN(numValue) ? 0 : numValue);
  };

  // Fetch groomer matrix data from API
  const fetchMatrixData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Get token from localStorage
      const token = localStorage.getItem("token") || "dummy-token";

      // Construct API URL with date range parameters
      const apiUrl = `/api/reports/groomer-matrix?start_date=${startDate}&end_date=${endDate}&token=${token}`;

      const response = await fetch(apiUrl);
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      // Process data for matrix display
      processMatrixData(data);
      setReportGenerated(true);
    } catch (err) {
      console.error("Error fetching matrix data:", err);
      setError(err.message || "Failed to fetch matrix data");
      
      // Use mock data for development/demo purposes
      generateMockData();
    } finally {
      setLoading(false);
    }
  };
  
  // Process data into the matrix format
  const processMatrixData = (rawData) => {
    if (!rawData || !Array.isArray(rawData) || rawData.length === 0) {
      // If no data, generate mock data for display purposes
      generateMockData();
      return;
    }
    
    // Group data by groomer and date
    const groomerMap = new Map();
    
    rawData.forEach(record => {
      const groomerName = record.firstName + ' ' + record.lastName;
      const apptDate = record.Appt_Date;
      
      if (!groomerMap.has(groomerName)) {
        groomerMap.set(groomerName, new Map());
      }
      
      // Store data for this groomer and date
      groomerMap.get(groomerName).set(apptDate, {
        apptCount: record.Appt_Count,
        petCount: record.Pet_Count,
        amount: record.Amount
      });
    });
    
    // Convert to array format for rendering
    const processedData = Array.from(groomerMap.entries()).map(([groomerName, dateMap]) => {
      const groomerData = { groomerName };
      
      // Add data for each date
      datesInRange.forEach(date => {
        const dateStr = format(date, 'yyyy-MM-dd');
        const data = dateMap.get(dateStr) || { apptCount: 0, petCount: 0, amount: 0 };
        groomerData[dateStr] = data;
      });
      
      return groomerData;
    });
    
    setMatrixData(processedData);
  };
  
  // Generate mock data for development/demo
  const generateMockData = () => {
    // Array of groomer names
    const groomers = [
      "John Smith",
      "Sarah Johnson",
      "Michael Williams",
      "Emily Brown",
      "James Davis"
    ];
    
    // Generate mock data for each groomer
    const mockData = groomers.map(groomerName => {
      const groomerData = { groomerName };
      
      // Add data for each date in range
      datesInRange.forEach(date => {
        const dateStr = format(date, 'yyyy-MM-dd');
        
        // Generate random data - ensure all days have data
        // especially the last day by setting a minimum value
        groomerData[dateStr] = {
          apptCount: Math.floor(Math.random() * 8) + 2, // Minimum 2 appointments
          petCount: Math.floor(Math.random() * 10) + 2, // Minimum 2 pets
          amount: Math.floor(Math.random() * 800) + 300 // Minimum $300
        };
      });
      
      return groomerData;
    });
    
    // Log the data for verification - focus on last day
    if (datesInRange.length > 0) {
      const lastDay = format(datesInRange[datesInRange.length - 1], 'yyyy-MM-dd');
      console.log(`Last day in range: ${lastDay} has data:`, 
        mockData.map(g => ({
          groomer: g.groomerName, 
          data: g[lastDay]
        }))
      );
    }
    
    setMatrixData(mockData);
  };

  // Handler for Run Report button
  const handleRunReport = () => {
    if (!isValid(new Date(startDate)) || !isValid(new Date(endDate))) {
      setError("Please select valid start and end dates");
      return;
    }
    
    // Parse dates with explicit pattern to avoid timezone issues
    const start = parse(startDate, 'yyyy-MM-dd', new Date());
    
    // For end date, ensure we include the full day
    let end = parse(endDate, 'yyyy-MM-dd', new Date());
    
    if (end < start) {
      setError("End date must be after or equal to start date");
      return;
    }
    
    console.log(`Running report for date range: ${startDate} to ${endDate}`);
    fetchMatrixData();
  };

  // Format currency for display
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };
  
  // Format percentage for display
  const formatPercentage = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'percent',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    }).format(value / 100);
  };
  
  // Check if running on iOS device
  const isIOS = () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  };
  
  // Force iOS Safari to repaint cells - helps with rendering issues
  const forceIOSRepaint = () => {
    if (isIOS() && datesInRange.length > 7) {
      // Small timeout to let the initial render complete
      setTimeout(() => {
        const tableContainer = document.querySelector('.matrix-table-container');
        if (tableContainer) {
          // Force a repaint by temporarily changing a style property
          tableContainer.style.opacity = '0.99';
          setTimeout(() => {
            tableContainer.style.opacity = '1';
          }, 50);
        }
      }, 300);
    }
  };
  
  // Apply force repaint when report is generated or dates change
  useEffect(() => {
    if (reportGenerated && !loading && matrixData.length > 0) {
      forceIOSRepaint();
    }
  }, [reportGenerated, loading, matrixData.length, datesInRange.length]);
  
  // Calculate totals for a specific date
  const calculateDateTotals = (date) => {
    const totals = {
      apptCount: 0,
      petCount: 0,
      amount: 0
    };
    
    matrixData.forEach(groomer => {
      const dateData = groomer[date];
      if (dateData) {
        totals.apptCount += dateData.apptCount;
        totals.petCount += dateData.petCount;
        totals.amount += dateData.amount;
      }
    });
    
    return totals;
  };
  
  // Calculate percentage of goal for a date
  const calculateGoalPercentage = (date) => {
    const totalAmount = calculateDateTotals(date).amount;
    
    // Only count groomers with non-zero price for this date
    const activeGroomersCount = matrixData.filter(groomer => {
      const dateData = groomer[date];
      return dateData && dateData.amount > 0;
    }).length;
    
    const totalGoal = dailyTarget * activeGroomersCount; // Target per active groomer * number of active groomers
    
    return totalGoal > 0 ? (totalAmount / totalGoal) * 100 : 0;
  };
  
  // Calculate total percentage booked across all dates
  const calculateTotalBookedPercentage = () => {
    let totalAmount = 0;
    let totalDaysWithActiveGroomers = 0;
    
    // Calculate for each date
    datesInRange.forEach(date => {
      const dateStr = format(date, 'yyyy-MM-dd');
      const dateTotals = calculateDateTotals(dateStr);
      totalAmount += dateTotals.amount;
      
      // Count active groomers for this date
      const activeGroomersCount = matrixData.filter(groomer => {
        const dateData = groomer[dateStr];
        return dateData && dateData.amount > 0;
      }).length;
      
      if (activeGroomersCount > 0) {
        totalDaysWithActiveGroomers += activeGroomersCount;
      }
    });
    
    const totalGoal = dailyTarget * totalDaysWithActiveGroomers;
    
    return totalGoal > 0 ? (totalAmount / totalGoal) * 100 : 0;
  };
  
  // Handle printing
  const handlePrint = () => {
    window.print();
  };

  return (
    <div>
      {/* Filter Controls - No Print */}
      <Paper className="no-print" sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Groomer Matrix Report
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Start Date"
                value={startDate ? parse(startDate, 'yyyy-MM-dd', new Date()) : null}
                onChange={(date) => setStartDate(date ? format(date, 'yyyy-MM-dd') : '')}
                renderInput={(params) => <TextField {...params} fullWidth required />}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="End Date"
                value={endDate ? parse(endDate, 'yyyy-MM-dd', new Date()) : null}
                onChange={(date) => setEndDate(date ? format(date, 'yyyy-MM-dd') : '')}
                renderInput={(params) => <TextField {...params} fullWidth required />}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Daily Target Amount"
              type="number"
              fullWidth
              value={dailyTarget}
              onChange={handleDailyTargetChange}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={3} container spacing={1}>
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                onClick={handleRunReport}
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Run Report"
                )}
              </Button>
            </Grid>
            {reportGenerated && (
              <Grid item>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handlePrint}
                  disabled={loading || matrixData.length === 0}
                  startIcon={<PrintIcon />}
                >
                  Print
                </Button>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Loading Indicator */}
      {loading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            my: 4,
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {/* Report Output */}
      {reportGenerated && !loading && matrixData.length > 0 && (
        <Box sx={{ mt: 3 }} id="print-container">
          {/* Report Header */}
          <Box sx={{ mb: 3 }}>
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid item xs>
                <Typography variant="h5" gutterBottom>
                  Groomer Matrix Report
                </Typography>
                <Typography variant="subtitle1" gutterBottom>
                  {startDate} to {endDate}
                </Typography>
                <Typography variant="subtitle2">
                  Daily Target: {formatCurrency(dailyTarget)} per groomer
                </Typography>
                {reportGenerated && matrixData.length > 0 && (
                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle2">
                      TOTAL % BOOKED: 
                    </Typography>
                    <Typography 
                      variant="subtitle2" 
                      sx={{ 
                        fontWeight: 'bold',
                        color: calculateTotalBookedPercentage() >= 100 
                          ? 'success.main' 
                          : (calculateTotalBookedPercentage() >= 90 
                            ? 'warning.main' 
                            : 'error.main')
                      }}
                    >
                      {formatPercentage(calculateTotalBookedPercentage())}
                    </Typography>
                  </Box>
                )}
              </Grid>
              <Grid item className="report-logo">
                <img 
                  src="/logo.png" 
                  alt="Company Logo" 
                  style={{ 
                    maxHeight: '60px', 
                    maxWidth: '150px',
                    marginRight: '20px'
                  }} 
                  onError={(e) => { e.target.style.display = 'none' }}
                />
              </Grid>
            </Grid>
            
            {/* Color Legend */}
            <Box sx={{ mt: 2, mb: 2, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Typography variant="subtitle2">Price Color Legend:</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 12, height: 12, bgcolor: 'success.main', borderRadius: '2px' }} />
                <Typography variant="caption">≥ 100% of Target</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 12, height: 12, bgcolor: 'warning.main', borderRadius: '2px' }} />
                <Typography variant="caption">≥ 90% of Target</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 12, height: 12, bgcolor: 'error.main', borderRadius: '2px' }} />
                <Typography variant="caption">Less than 90% of Target</Typography>
              </Box>
            </Box>
            
            <Divider sx={{ mt: 2 }} />
          </Box>

          {/* Matrix Table */}
          <Box sx={{ mb: 2 }}></Box>
          {/* Table with fixed header */}
          <Paper 
            elevation={2}
            sx={{ 
              overflow: 'hidden',
              width: '100%',
              borderRadius: 1
            }}
          >
            <Box
              sx={{
                maxHeight: '70vh',
              overflowY: 'auto',
              overflowX: 'auto',
              // For iOS momentum scrolling
              WebkitOverflowScrolling: 'touch',
              '&::-webkit-scrollbar': {
                width: '10px',
                height: '10px'
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0,0,0,0.2)',
                borderRadius: '10px'
              },
              // Ensure scrollbar is always visible on desktop
              msOverflowStyle: 'auto',  // IE and Edge
              scrollbarWidth: 'auto'    // Firefox
              }}
              className="matrix-table-container"
            >
              <Table 
                stickyHeader 
                sx={{ 
                  borderCollapse: 'separate',
                  minWidth: '100%',
                  width: 'max-content', // Ensure table expands to fit all columns
                  '& .MuiTableCell-root': {
                    whiteSpace: 'nowrap',
                    minWidth: '104px', // 20% reduction from 130px
                    padding: '8px 4px' // Slightly tighter padding
                  }
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell 
                        sx={{ 
                          position: 'sticky', 
                          left: 0, 
                          zIndex: 3,
                          backgroundColor: '#f3f3f3',
                          boxShadow: '2px 0px 3px -1px rgba(0,0,0,0.1)',
                          minWidth: '120px' // Slightly wider for groomer names column
                        }}
                    >
                      <strong>Groomer</strong>
                    </TableCell>
                    {datesInRange.map(date => (
                      <TableCell 
                        key={format(date, 'yyyy-MM-dd')} 
                        align="center"
                        sx={{
                          backgroundColor: '#f3f3f3',
                          boxShadow: '0px 2px 3px -1px rgba(0,0,0,0.1)'
                        }}
                      >
                        <Typography variant="body2" fontWeight="bold">
                          {format(date, 'MM/dd/yyyy')}
                        </Typography>
                        <Typography variant="caption">
                          {format(date, 'EEE')}
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Groomer rows */}
                  {matrixData.map((groomer, index) => (
                    <TableRow key={index} hover>
                      <TableCell 
                        sx={{ 
                          position: 'sticky', 
                          left: 0, 
                          zIndex: 1,
                          backgroundColor: 'white',
                          boxShadow: '2px 0px 3px -1px rgba(0,0,0,0.1)',
                          minWidth: '120px' // Slightly wider for groomer names column
                        }}
                      >
                        <strong>{groomer.groomerName}</strong>
                      </TableCell>
                      {datesInRange.map(date => {
                        const dateStr = format(date, 'yyyy-MM-dd');
                        const data = groomer[dateStr] || { apptCount: 0, petCount: 0, amount: 0 };
                        return (
                          <TableCell 
                            key={dateStr} 
                            align="center"
                          >
                            {(data.amount > 0 || data.apptCount > 0 || data.petCount > 0) ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    fontWeight: 'bold',
                                    color: data.amount >= dailyTarget 
                                      ? 'success.main' 
                                      : (data.amount >= dailyTarget * 0.9 
                                        ? 'warning.main' 
                                        : 'error.main')
                                  }}
                                >
                                  {formatCurrency(data.amount)}
                                </Typography>
                                <Typography variant="caption">
                                  Appts: {data.apptCount}
                                </Typography>
                                <Typography variant="caption">
                                  Pets: {data.petCount}
                                </Typography>
                              </Box>
                            ) : (
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                -
                              </Typography>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                  
                  {/* Totals row */}
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell 
                      sx={{ 
                        position: 'sticky', 
                        left: 0, 
                        zIndex: 1,
                        backgroundColor: '#f5f5f5',
                        boxShadow: '2px 0px 3px -1px rgba(0,0,0,0.1)',
                        minWidth: '120px' // Slightly wider for totals column
                      }}
                    >
                      <strong>TOTALS</strong>
                    </TableCell>
                    {datesInRange.map(date => {
                      const dateStr = format(date, 'yyyy-MM-dd');
                      const totals = calculateDateTotals(dateStr);
                      return (
                        <TableCell 
                          key={`total-${dateStr}`} 
                          align="center"
                        >
                          {(totals.amount > 0 || totals.apptCount > 0 || totals.petCount > 0) ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {formatCurrency(totals.amount)}
                              </Typography>
                              <Typography variant="caption">
                                Appts: {totals.apptCount}
                              </Typography>
                              <Typography variant="caption">
                                Pets: {totals.petCount}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              -
                            </Typography>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                  
                  {/* Percentage of Goal row */}
                  <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                    <TableCell 
                      sx={{ 
                        position: 'sticky', 
                        left: 0, 
                        zIndex: 1,
                        backgroundColor: '#e3f2fd',
                        boxShadow: '2px 0px 3px -1px rgba(0,0,0,0.1)',
                        minWidth: '120px' // Slightly wider for percentage column
                      }}
                    >
                      <strong>% OF DAILY GOAL</strong>
                    </TableCell>
                    {datesInRange.map(date => {
                      const dateStr = format(date, 'yyyy-MM-dd');
                      const percentage = calculateGoalPercentage(dateStr);
                      return (
                        <TableCell 
                          key={`goal-${dateStr}`} 
                          align="center"
                          sx={{ 
                            color: percentage >= 100 ? 'success.main' : (percentage >= 85 ? 'warning.main' : 'error.main')
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {formatPercentage(percentage)}
                          </Typography>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </Paper>
          
          {/* Report Footer */}
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="textSecondary">
              Report generated: {format(new Date(), 'MM/dd/yyyy hh:mm:ss a')}
            </Typography>
          </Box>
        </Box>
      )}
      
      {/* No Data Message */}
      {reportGenerated && !loading && matrixData.length === 0 && !error && (
        <Typography align="center" color="textSecondary" sx={{ mt: 4, mb: 4 }}>
          No data found for the selected date range.
        </Typography>
      )}
    </div>
  );
};

export default GroomerMatrix;